// TopBar — v2 chrome: typographic FACET wordmark, source switcher, the
// LEDGER / DIFF / IMPACT navigation, flow + scenario context chips, and the
// staged-canvas stage toggle. Verdict chips always come from real run
// results (scenarioBadges.deriveEffectiveVerdict) — never from scenario kind.
//
// With the v1 drawer gone, the chips are the primary nav back into the
// selection surfaces (design: every ▾ chip opens what it names):
//   · source chip  ▾ — the loaded source's flows (count + earned verdict),
//     plus "Portfolio ledger" and "Switch source…" (landing)
//   · flow chip    ▾ — that flow's ledger
//   · scenario chip ▾ — the flow's scenarios (kind glyph + verdict) for
//     direct lateral switching; not-loaded files are disabled with a reason.

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAppView,
  setAppView,
  openFlowLedger,
  selectViewFlowId,
  selectScenarioStage,
  setScenarioStage,
} from '../../store/slices/uiSlice.ts';
import {
  selectManifest,
  selectActiveSource,
  selectActiveScenarioId,
  selectWebSocketState,
} from '../../store/slices/scenarioSourceSlice.ts';
import {
  selectSimulationStatus,
  selectEffectiveAssertionSummary,
  selectScenarioVerdict,
  selectSuiteResults,
} from '../../store/slices/simulationSlice.ts';
import { switchScenario } from '../../store/sagas/scenarioSaga.ts';
import type { RootState } from '../../store/index.ts';
import {
  classifyScenarioType,
  deriveEffectiveVerdict,
  KIND_GLYPHS,
  TYPE_CHIP_STYLES,
  VERDICT_CHIP_STYLES,
} from './scenarioBadges.ts';
import {
  sourceDisplayLabel,
  splitVariant,
  verdictMapFromSuite,
  tallyVerdicts,
  ledgerVerdict,
  LEDGER_VERDICT_LABELS,
  scenarioAvailability,
} from '../v2/ledgerData.ts';
import { useFeatureGate } from '../../hooks/useFeatureGate.ts';

type OpenMenu = 'source' | 'scenario' | null;

export default function TopBar() {
  const dispatch = useDispatch();
  const view = useSelector(selectAppView);
  const stage = useSelector(selectScenarioStage);
  const manifest = useSelector(selectManifest);
  const activeSource = useSelector(selectActiveSource);
  const activeScenarioId = useSelector(selectActiveScenarioId);
  const viewFlowId = useSelector(selectViewFlowId);
  const activeFlowId = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].activeFlowId,
  );
  const loadedTopologies = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].loadedTopologies,
  );
  const loadedScenarios = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].loadedScenarios,
  );
  const wsState = useSelector(selectWebSocketState);
  const status = useSelector(selectSimulationStatus);
  const summary = useSelector(selectEffectiveAssertionSummary);
  const storeVerdict = useSelector(selectScenarioVerdict);
  const suiteResults = useSelector(selectSuiteResults);
  const canMultiScenario = useFeatureGate('multiScenario');

  const [menu, setMenu] = useState<OpenMenu>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Any open menu closes on outside pointer-down or Escape.
  useEffect(() => {
    if (menu === null) return;
    const onPointerDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setMenu(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menu]);

  const hasSource = Boolean(manifest) && view !== 'landing';
  // The flow chip names the ledger being READ (view 'flow'), otherwise the
  // active scenario's flow.
  const chipFlowId = view === 'flow' ? (viewFlowId ?? activeFlowId) : activeFlowId;
  const flow = manifest?.flows.find((f) => f.flowId === chipFlowId) ?? null;

  let entry = null;
  if (manifest && activeScenarioId) {
    for (const f of manifest.flows) {
      const e = f.scenarios.find((s) => s.scenarioId === activeScenarioId);
      if (e) {
        entry = e;
        break;
      }
    }
  }
  const kind = entry ? classifyScenarioType(entry) : null;
  const verdict = deriveEffectiveVerdict(status, summary, storeVerdict);
  const verdictChip = verdict !== null ? VERDICT_CHIP_STYLES[verdict] : null;
  const inScenario = view === 'scenario';

  const shortLabel = entry ? splitVariant(entry.scenarioLabel) : null;

  // Verdicts for the nav menus come ONLY from real suite rows — a flow or
  // scenario with no rows renders without a verdict, never green.
  const suiteVerdicts = verdictMapFromSuite(suiteResults);

  return (
    <div className="fv2-topbar" ref={barRef}>
      <button
        type="button"
        className="fv2-wordmark"
        title="Facet"
        onClick={() => dispatch(setAppView(manifest ? 'portfolio' : 'landing'))}
      >
        <span className="fv2-wordmark-text">facet</span>
        <span className="fv2-wordmark-dot" />
      </button>

      {hasSource && manifest && (
        <>
          <span className="fv2-menu-anchor">
            <button
              type="button"
              className="fv2-source-btn"
              title="Loaded flows · switch source"
              aria-haspopup="menu"
              aria-expanded={menu === 'source'}
              onClick={() => setMenu(menu === 'source' ? null : 'source')}
            >
              <span style={{ color: 'var(--accent)' }}>●</span>{' '}
              {sourceDisplayLabel(activeSource, wsState.specFile)} ▾
            </button>
            {menu === 'source' && (
              <div className="fv2-menu" role="menu" aria-label="Loaded flows">
                <div className="fv2-menu-label">LOADED FLOWS · {manifest.flows.length}</div>
                {manifest.flows.map((f) => {
                  const flowVerdict = ledgerVerdict(tallyVerdicts(f.scenarios, suiteVerdicts));
                  return (
                    <button
                      key={f.flowId}
                      type="button"
                      role="menuitem"
                      className="fv2-menu-item"
                      title={`Open the ${f.flowName} flow ledger`}
                      onClick={() => {
                        setMenu(null);
                        dispatch(openFlowLedger(f.flowId));
                      }}
                    >
                      <span className="fv2-menu-item-name">{f.flowName}</span>
                      <span className="fv2-menu-meta">
                        {f.scenarios.length} SCENARIO{f.scenarios.length === 1 ? '' : 'S'}
                      </span>
                      {flowVerdict !== null && (
                        <span
                          className="fv2-menu-verdict"
                          style={{ color: LEDGER_VERDICT_LABELS[flowVerdict].color }}
                          title="Verdict from the last suite run"
                        >
                          {LEDGER_VERDICT_LABELS[flowVerdict].label}
                        </span>
                      )}
                    </button>
                  );
                })}
                <div className="fv2-menu-sep" />
                <button
                  type="button"
                  role="menuitem"
                  className="fv2-menu-item"
                  onClick={() => {
                    setMenu(null);
                    dispatch(setAppView('portfolio'));
                  }}
                >
                  <span className="fv2-menu-item-name">Portfolio ledger</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="fv2-menu-item"
                  onClick={() => {
                    setMenu(null);
                    dispatch(setAppView('landing'));
                  }}
                >
                  <span className="fv2-menu-item-name">Switch source…</span>
                </button>
              </div>
            )}
          </span>
          <button
            type="button"
            className={`fv2-nav-btn${view === 'portfolio' ? ' fv2-nav-on' : ''}`}
            onClick={() => dispatch(setAppView('portfolio'))}
          >
            LEDGER
          </button>
          {canMultiScenario && (
            <button
              type="button"
              className={`fv2-nav-btn${view === 'diff' ? ' fv2-nav-on' : ''}`}
              onClick={() => dispatch(setAppView('diff'))}
            >
              DIFF
            </button>
          )}
          <button
            type="button"
            className={`fv2-nav-btn${view === 'impact' ? ' fv2-nav-on' : ''}`}
            onClick={() => dispatch(setAppView('impact'))}
          >
            IMPACT
          </button>
        </>
      )}

      {hasSource && flow && (view === 'flow' || inScenario) && (
        <button
          type="button"
          className="fv2-flow-chip"
          title="Open this flow's ledger"
          onClick={() => dispatch(openFlowLedger(flow.flowId))}
        >
          {flow.flowName} ▾
        </button>
      )}

      {inScenario && entry && kind && (
        <span className="fv2-menu-anchor">
          <button
            type="button"
            className="fv2-scenario-chip"
            title="Switch scenario within this flow"
            aria-haspopup="menu"
            aria-expanded={menu === 'scenario'}
            onClick={() => setMenu(menu === 'scenario' ? null : 'scenario')}
          >
            <span style={{ color: TYPE_CHIP_STYLES[kind].color }} title={`kind: ${kind}`}>
              {KIND_GLYPHS[kind]}
            </span>
            <b title={entry.scenarioLabel}>
              {shortLabel?.variant
                ? `${kind.toUpperCase()} ${shortLabel.variant}`
                : entry.scenarioLabel}
            </b>
            {verdictChip && (
              <span
                style={{ color: verdictChip.color }}
                title={
                  verdict === 'error'
                    ? 'Last run aborted before completing'
                    : verdict === 'none'
                      ? 'Last run completed with no assertions'
                      : 'Verdict from the last run'
                }
              >
                {verdict === 'pass'
                  ? '✓ '
                  : verdict === 'fail'
                    ? '✗ '
                    : verdict === 'error'
                      ? '⚠ '
                      : ''}
                {verdictChip.label}
              </span>
            )}
            <span aria-hidden="true">▾</span>
          </button>
          {menu === 'scenario' && flow && (
            <div className="fv2-menu" role="menu" aria-label="Scenarios in this flow">
              <div className="fv2-menu-label">
                {flow.flowName} · {flow.scenarios.length} SCENARIO
                {flow.scenarios.length === 1 ? '' : 'S'}
              </div>
              {flow.scenarios.map((e) => {
                const isActive = e.scenarioId === activeScenarioId;
                const k = classifyScenarioType(e);
                const v = suiteVerdicts[e.scenarioId] ?? null;
                const rowChip = v !== null ? VERDICT_CHIP_STYLES[v] : null;
                const avail = scenarioAvailability(
                  activeSource,
                  flow,
                  e,
                  loadedTopologies,
                  loadedScenarios,
                );
                const { base, variant } = splitVariant(e.scenarioLabel);
                return (
                  <button
                    key={e.scenarioId}
                    type="button"
                    role="menuitem"
                    className={`fv2-menu-item${isActive ? ' fv2-menu-item-on' : ''}`}
                    disabled={!avail.ok}
                    aria-current={isActive || undefined}
                    title={avail.ok ? e.description : avail.reason}
                    onClick={() => {
                      setMenu(null);
                      if (!isActive) {
                        dispatch(switchScenario({ source: activeSource, flow, entry: e }));
                      }
                    }}
                  >
                    <span style={{ color: TYPE_CHIP_STYLES[k].color }} title={`kind: ${k}`}>
                      {KIND_GLYPHS[k]}
                    </span>
                    <span className="fv2-menu-item-name">
                      {base}
                      {variant ? ` ${variant}` : ''}
                    </span>
                    {rowChip ? (
                      <span
                        className="fv2-menu-verdict"
                        style={{ color: rowChip.color }}
                        title="Verdict from the last run"
                      >
                        {v === 'pass' ? '✓ ' : v === 'fail' ? '✗ ' : v === 'error' ? '⚠ ' : ''}
                        {rowChip.label}
                      </span>
                    ) : (
                      <span
                        className="fv2-menu-verdict fv2-dim"
                        title="This scenario has not run yet"
                      >
                        NOT RUN
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </span>
      )}

      <div className="fv2-topbar-spacer" />

      {inScenario && (
        <>
          <div className="fv2-stage-toggle" role="group" aria-label="Reading stage">
            <button
              type="button"
              className={`fv2-stage-btn${stage === 1 ? ' fv2-stage-on' : ''}`}
              onClick={() => dispatch(setScenarioStage(1))}
            >
              <span className="fv2-stage-k">1</span>VERDICT
            </button>
            <button
              type="button"
              className={`fv2-stage-btn${stage === 2 ? ' fv2-stage-on' : ''}`}
              onClick={() => dispatch(setScenarioStage(2))}
            >
              <span className="fv2-stage-k">2</span>STORY &amp; EVIDENCE
            </button>
          </div>
        </>
      )}
    </div>
  );
}
